import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { digestLabels, timezoneLabel } from '../lib/format';
import { fieldErrors } from '../lib/form-errors';
import { haptic } from '../lib/telegram';
import type { NotificationDigest, NotificationSettings } from '../types';
import { Button, ErrorNotice, Field, Input, LoadingScreen, Select } from './ui';

const FIELD_LABELS: Record<string, string> = {
  timezone: 'Часовой пояс',
  sendFrom: 'Начало окна',
  sendTo: 'Конец окна',
  deadlineLeadDays: 'За сколько дней предупредить',
  digest: 'Напоминания о заказах',
};

const DIGEST_OPTIONS: NotificationDigest[] = ['OFF', 'DAILY', 'EVERY_3_DAYS', 'WEEKLY'];

/**
 * Настройки уведомлений. Одна форма на оба кабинета: у художника часовой пояс
 * уже задан в профиле, поэтому здесь его не спрашивают, а заказчику задать его
 * больше негде.
 */
export function NotificationSettingsForm({
  showTimezone,
  onSaved,
}: {
  showTimezone: boolean;
  /** Вызывается после успешного сохранения: в модальном окне закрывает его. */
  onSaved?: () => void;
}) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['notification-settings'],
    queryFn: () => api<NotificationSettings>('/notification-settings'),
  });

  if (query.isPending) return <LoadingScreen label="Открываем настройки" />;
  if (query.error) return <ErrorNotice error={query.error} />;

  return (
    <SettingsFields
      settings={query.data}
      showTimezone={showTimezone}
      closeOnSave={onSaved !== undefined}
      onSaved={(updated) => {
        queryClient.setQueryData(['notification-settings'], updated);
        onSaved?.();
      }}
    />
  );
}

function SettingsFields({
  settings,
  showTimezone,
  closeOnSave,
  onSaved,
}: {
  settings: NotificationSettings;
  showTimezone: boolean;
  closeOnSave: boolean;
  onSaved: (updated: NotificationSettings) => void;
}) {
  const [aroundTheClock, setAroundTheClock] = useState(settings.aroundTheClock);
  const [sendFrom, setSendFrom] = useState(settings.sendFromLabel);
  const [sendTo, setSendTo] = useState(settings.sendToLabel);
  const [timezone, setTimezone] = useState(settings.timezone ?? '');
  const [statusUpdates, setStatusUpdates] = useState(settings.statusUpdates);
  const [deadlineReminders, setDeadlineReminders] = useState(settings.deadlineReminders);
  const [paymentReminders, setPaymentReminders] = useState(settings.paymentReminders);
  const [leadDays, setLeadDays] = useState(String(settings.deadlineLeadDays));
  const [digest, setDigest] = useState<NotificationDigest>(settings.digest);

  const [saved, setSaved] = useState(false);

  // Сохранённые значения снова становятся исходными: форма живёт между заходами.
  useEffect(() => {
    setAroundTheClock(settings.aroundTheClock);
    setSendFrom(settings.sendFromLabel);
    setSendTo(settings.sendToLabel);
    setTimezone(settings.timezone ?? '');
    setStatusUpdates(settings.statusUpdates);
    setDeadlineReminders(settings.deadlineReminders);
    setPaymentReminders(settings.paymentReminders);
    setLeadDays(String(settings.deadlineLeadDays));
    setDigest(settings.digest);
  }, [settings]);

  useEffect(() => {
    if (!saved) {
      return;
    }

    const timer = setTimeout(() => setSaved(false), 2500);
    return () => clearTimeout(timer);
  }, [saved]);

  const mutation = useMutation({
    mutationFn: () =>
      api<NotificationSettings>('/notification-settings', {
        method: 'PATCH',
        body: {
          // Круглосуточная доставка — это совпадающие границы окна.
          sendFrom,
          sendTo: aroundTheClock ? sendFrom : sendTo,
          ...(showTimezone ? { timezone: timezone.trim() || null } : {}),
          statusUpdates,
          deadlineReminders,
          deadlineLeadDays: Number(leadDays),
          paymentReminders,
          digest,
        },
      }),
    onSuccess: (updated) => {
      haptic('success');
      // Отдельным экраном закрывать нечего, поэтому там нужен видимый ответ.
      if (!closeOnSave) {
        setSaved(true);
      }
      onSaved(updated);
    },
  });
  const errors = fieldErrors(mutation.error);
  const leadDaysValid = /^\d+$/.test(leadDays) && Number(leadDays) <= 14;

  return (
    <form
      className="stack"
      onSubmit={(event) => {
        event.preventDefault();
        mutation.mutate();
      }}
    >
      <Field
        label="Доставлять уведомления"
        hint={`Часовой пояс: ${timezoneLabel(settings.effectiveTimezone)}`}
      >
        <Select
          value={aroundTheClock ? 'always' : 'window'}
          onChange={(event) => setAroundTheClock(event.target.value === 'always')}
        >
          <option value="window">В выбранные часы</option>
          <option value="always">Круглосуточно</option>
        </Select>
      </Field>

      {!aroundTheClock && (
        <div className="form-grid">
          <Field label="С" error={errors.sendFrom}>
            <Input
              type="time"
              required
              value={sendFrom}
              onChange={(event) => setSendFrom(event.target.value)}
            />
          </Field>
          <Field label="До" error={errors.sendTo}>
            <Input
              type="time"
              required
              value={sendTo}
              onChange={(event) => setSendTo(event.target.value)}
            />
          </Field>
        </div>
      )}

      {showTimezone && (
        <Field label="Часовой пояс" hint="Регион или сдвиг: +7" error={errors.timezone}>
          <Input
            value={timezone}
            placeholder="Europe/Moscow"
            onChange={(event) => setTimezone(event.target.value)}
          />
        </Field>
      )}

      <p className="muted settings-note">
        Личные сообщения по заказу приходят сразу, мимо окна тишины.
      </p>

      <Toggle
        label="Смена статуса и этапа заказа"
        checked={statusUpdates}
        onChange={setStatusUpdates}
      />
      <Toggle
        label="Напоминание о скором дедлайне"
        checked={deadlineReminders}
        onChange={setDeadlineReminders}
      />

      {deadlineReminders && (
        <Field
          label="За сколько дней предупредить"
          hint="За сколько дней предупредить. 0 — в сам день"
          error={leadDaysValid ? errors.deadlineLeadDays : 'От 0 до 14'}
        >
          <Input
            type="number"
            min={0}
            max={14}
            value={leadDays}
            onChange={(event) => setLeadDays(event.target.value)}
          />
        </Field>
      )}

      <Toggle
        label="Остаток по крупному заказу"
        checked={paymentReminders}
        onChange={setPaymentReminders}
      />

      <Field
        label="Напоминать о заказах в работе"
        hint="Короткое напоминание о том, что в работе, даже когда изменений нет"
        error={errors.digest}
      >
        <Select
          value={digest}
          onChange={(event) => setDigest(event.target.value as NotificationDigest)}
        >
          {DIGEST_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {digestLabels[option]}
            </option>
          ))}
        </Select>
      </Field>

      {mutation.error && <ErrorNotice error={mutation.error} labels={FIELD_LABELS} />}
      {saved && <p className="notice notice--success">Настройки сохранены</p>}
      <Button type="submit" wide disabled={!leadDaysValid || mutation.isPending}>
        {mutation.isPending ? 'Сохраняем…' : 'Сохранить'}
      </Button>
    </form>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="toggle-row">
      <span>{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        className={`switch ${checked ? 'is-on' : ''}`}
        onClick={() => onChange(!checked)}
      >
        <span />
      </button>
    </label>
  );
}
